import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WS_CON_STATE } from "../../pages/Room/inv-ws";
import { signOut } from "../common-actions";
import { UserIdPayload } from "./common-payloads";
import {
  RoomInfoPayload,
  NewRemoteHolderPayload,
  ChatMessagePayload,
  ConnectedPayload,
  ShowUserContextMenuPayload,
  UserJoinedPayload,
  UserLeftPayload,
  ChatMessageConfirmedPayload,
  ChatMessageIdPayload,
  RoomClosedPayload,
  VolumePayload,
  ConnectErrorPayload,
  UserProfileImagePayload,
  UserDisplayNamePayload,
  RfConStatePayload,
  ChatMessageImgPayload,
  UserJoinedVcPayload,
} from "./room-payloads";
import { ChatMessage, RemoteRequester, RoomUser } from "./room-types";

interface RoomUsers {
  [userId: string]: RoomUser;
}

interface BannedUser {
  id: string;
  username: string;
}

interface Admin {
  id: string;
  profileImageUrl: string;
  username: string;
}

interface RoomState {
  self: {
    id: string;
    displayName: string;
    profileImageUrl: string;
    color: string;
  };
  id: string;
  connected: boolean;
  admin: Admin;
  title: string;
  proxy: {
    state: "off" | "on";
    userId: string | null;
  };
  overlay: {
    show: boolean;
    stage:
      | "loading"
      | "buffering"
      | "muted"
      | "error"
      | "done"
      | "disconnected";
    message: string;
  };
  userContextMenu: {
    userId: string;
    show: boolean;
    elementId: string;
    anchorOrigin: {
      vertical: "top" | "center" | "bottom";
      horizontal: "left" | "center" | "right";
    };
    transformOrigin: {
      vertical: "top" | "center" | "bottom";
      horizontal: "left" | "center" | "right";
    };
  };
  alert: {
    message: string;
    show: boolean;
    severity: "error" | "warning" | "info" | "success";
    vertical: "bottom" | "top";
    horizontal: "left" | "center" | "right";
  };
  volume: number;
  volumeTouched: boolean;
  theaterMode: boolean;
  fullscreen: boolean;
  remoteHolderId: string | null;
  requestingRemote: boolean;
  users: RoomUsers;
  bannedUsers: BannedUser[];
  chatVisible: boolean;
  chatMessages: ChatMessage[];
  remoteRequesters: RemoteRequester[];
  vcUserIds: string[];
  vcEnabled: boolean;
  inVidChat: boolean;
  castleConState: WS_CON_STATE;
}

const initialState: RoomState = {
  id: "",
  connected: false,
  self: {
    id: "",
    displayName: "",
    profileImageUrl: "",
    color: "#fcfcfc",
  },
  admin: {
    id: "",
    profileImageUrl: "",
    username: "",
  },
  title: "",
  proxy: {
    state: "off",
    userId: null,
  },
  overlay: {
    show: true,
    stage: "loading",
    message: "Connecting...",
  },
  userContextMenu: {
    userId: "",
    show: false,
    elementId: null,
    anchorOrigin: {
      vertical: "center",
      horizontal: "right",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  },
  alert: {
    message: "",
    show: false,
    severity: "info",
    vertical: "top",
    horizontal: "right",
  },
  volume: 0,
  volumeTouched: false,
  theaterMode: false,
  fullscreen: false,
  remoteHolderId: null,
  requestingRemote: false,
  users: {},
  bannedUsers: [],
  chatVisible: true,
  chatMessages: [],
  remoteRequesters: [],
  vcUserIds: [],
  vcEnabled: true,
  inVidChat: false,
  castleConState: WS_CON_STATE.CONNECTING,
};

export const roomSlice = createSlice({
  name: "room",
  initialState,
  reducers: {
    mutedOverlay: (state) => {
      state.overlay = {
        show: true,
        stage: "muted",
        message: "",
      };
    },
    doneOverlay: (state) => {
      state.overlay = {
        show: false,
        stage: "done",
        message: "",
      };
    },
    failedToConnect: (state, action: PayloadAction<string>) => {
      state.overlay = {
        show: true,
        stage: "error",
        message: action.payload,
      };
    },
    initialUnmute: (state, action: PayloadAction<VolumePayload>) => {
      state.overlay = {
        show: false,
        stage: "done",
        message: "",
      };
      state.volume = action.payload.volume;
    },
    buffering: (state) => {
      state.overlay.message = "Buffering";
    },
    //todo: deprecated??
    connected: (state, action: PayloadAction<ConnectedPayload>) => {
      const { userId, username, profileImageUrl } = action.payload;
      state.self = {
        userId: userId,
        username: username,
        profileImageUrl: profileImageUrl,
      };
    },
    connectError: (state, action: PayloadAction<ConnectErrorPayload>) => {
      state.overlay.message = action.payload.message;
      state.overlay.show = true;
      state.overlay.stage = "error";
    },
    roomInfo: (state, action: PayloadAction<RoomInfoPayload>) => {
      const payload = action.payload;
      state.connected = true;
      state.admin = {
        id: payload.adminId,
        username: "",
        profileImageUrl: "",
      };
      state.title = "";
      state.vcUserIds = [];
      state.remoteHolderId = payload.remoteHolderId;

      state.users = {}; // clear users in case this is reconnection.
      payload.users.forEach((user) => {
        const roomUser: RoomUser = {
          id: user.id,
          username: user.username,
          displayName: user.displayName,
          profileImageUrl: user.profileImageUrl,
          adminMuted: false,
          color: user.color || "#ffffff",
          selfMuted: false,
          localMuted: false,
          vc: {
            audioEnabled: false,
            audioAdminDisabled: false,
            audioLocalDisabled: false,
            videoEnabled: false,
            videoAdminDisabled: false,
            videoLocalDisabled: false,
          },
        };
        state.users[user.id] = roomUser;
      });
      state.bannedUsers = [];

      state.self = {
        id: payload.self.id,
        displayName: payload.self.displayName,
        color: "",
        profileImageUrl: payload.self.profileImageUrl,
      };
    },
    showChat: (state) => {
      state.chatVisible = true;
    },
    hideChat: (state) => {
      state.chatVisible = false;
    },
    hideUserContextMenu: (state) => {
      state.userContextMenu.userId = "";
      state.userContextMenu.show = false;
      state.userContextMenu.elementId = "";
    },
    showUserContextMenu: (
      state,
      action: PayloadAction<ShowUserContextMenuPayload>
    ) => {
      const payload = action.payload;
      state.userContextMenu = {
        elementId: payload.elementId,
        userId: payload.userId,
        show: true,
        anchorOrigin: {
          vertical: payload.anchorOrigin.vertical,
          horizontal: payload.anchorOrigin.horizontal,
        },
        transformOrigin: {
          vertical: payload.transformOrigin.vertical,
          horizontal: payload.transformOrigin.horizontal,
        },
      };
    },
    volume: (state, action: PayloadAction<VolumePayload>) => {
      state.volume = action.payload.volume;
      state.volumeTouched = true;
      if (state.overlay.stage === "muted") {
        state.overlay.stage = "done";
        state.overlay.show = false;
        state.overlay.message = "";
      }
    },
    theaterModeOn: (state) => {
      state.theaterMode = true;
      state.fullscreen = false;
    },
    theaterModeOff: (state) => {
      state.theaterMode = false;
      state.fullscreen = false;
    },
    fullscreenOn: (state) => {
      state.fullscreen = true;
      state.theaterMode = false;
    },
    fullscreenOff: (state) => {
      state.fullscreen = false;
      state.theaterMode = false;
    },
    userJoined: (state, action: PayloadAction<UserJoinedPayload>) => {
      const {
        userId,
        username,
        displayName,
        color,
        profileImageUrl,
        adminMuted,
        vc,
      } = action.payload;
      state.users[userId] = {
        id: userId,
        username: username,
        displayName: displayName,
        profileImageUrl: profileImageUrl,
        adminMuted: adminMuted,
        requestingRemote: false,
        color: color,
        selfMuted: false,
        localMuted: false,
        vc: {
          audioEnabled: false,
          audioAdminDisabled: vc.audioAdminDisabled,
          audioLocalDisabled: vc.audioLocalDisabled,
          videoEnabled: false,
          videoAdminDisabled: vc.videoAdminDisabled,
          videoLocalDisabled: vc.videoLocalDisabled,
        },
      };
    },
    userLeft: (state, action: PayloadAction<UserLeftPayload>) => {
      delete state.users[action.payload.userId];
      state.vcUserIds = state.vcUserIds.filter(
        (id) => id !== action.payload.userId
      );
    },
    newRemoteHolder: (state, action: PayloadAction<NewRemoteHolderPayload>) => {
      state.remoteHolderId = action.payload.userId;
      state.remoteRequesters = [];
      state.requestingRemote = false;
    },
    requestRemote: (state) => {
      state.requestingRemote = true;
    },
    cancelledRequestingRemote: (state) => {
      state.requestingRemote = false;
    },
    remoteRequestDenied: (state) => {
      state.requestingRemote = false;
    },
    incomingRemoteRequest: (state, action: PayloadAction<UserIdPayload>) => {
      const id = action.payload.userId;
      for (let i = 0; i < state.remoteRequesters.length; i++) {
        if (state.remoteRequesters[i].userId === action.payload.userId) {
          return;
        }
      }

      const user = state.users[action.payload.userId];
      if (!user) {
        return;
      }

      state.remoteRequesters.push({
        userId: user.id,
        userColor: user.color,
        displayName: user.displayName,
        username: user.username
      });
    },
    incomingRemoteRequestGranted: (
      state,
      action: PayloadAction<UserIdPayload>
    ) => {
      state.remoteRequesters = [];
      state.remoteHolderId = action.payload.userId;
    },
    incomingRemoteRequestCancelled: (
      state,
      action: PayloadAction<UserIdPayload>
    ) => {
      state.remoteRequesters = state.remoteRequesters.filter(
        (req) => req.userId !== action.payload.userId
      );
    },
    roomClosed: (state, action: PayloadAction<RoomClosedPayload>) => {
      state.connected = false;
      state.overlay.show = true;
      state.overlay.message = action.payload.msg;
      state.overlay.stage = "disconnected";
      state.inVidChat = false;
      state.remoteHolderId = null;
      state.users = {};
    },
    kicked: (state) => {
      state.overlay = {
        show: true,
        stage: "disconnected",
        message: "You have been kicked from the room.",
      };
      state.connected = false;
      state.inVidChat = false;
      state.remoteHolderId = "";
    },
    kickedFromVc: (state) => {
      const userId = state.self.id;
      state.vcUserIds = state.vcUserIds.filter((id) => id !== userId);
      const user = state.users[userId];
      if (user) {
        user.vc.audioEnabled = false;
        user.vc.videoEnabled = false;
      }
      state.inVidChat = false;
    },
    banned: (state) => {
      state.overlay = {
        show: true,
        stage: "disconnected",
        message: "You have been banned from the room.",
      };
      state.connected = false;
      state.inVidChat = false;
      state.remoteHolderId = "";
    },
    chatMessage: (state, action: PayloadAction<ChatMessagePayload>) => {
      const { id, content, createdAt, user, localId, contentType } =
        action.payload;
      const msg: ChatMessage = {
        id: id,
        localId: localId,
        content: content,
        contentType: contentType,
        createdAt: createdAt,
        user: {
          id: user.id,
          displayName: user.displayName,
          profileImageUrl: user.profileImageUrl,
        },
      };
      state.chatMessages.push(msg);
      if (state.chatMessages.length > 50) {
        state.chatMessages.shift();
      }
    },
    chatMessageConfirmed: (
      state,
      action: PayloadAction<ChatMessageConfirmedPayload>
    ) => {
      const { localId, id } = action.payload;
      const cm = state.chatMessages;
      for (let i = cm.length - 1; i >= 0; i--) {
        if (cm[i].localId === localId) {
          cm[i].id = id;
          return;
        }
      }
    },
    chatMessageImg: (state, action: PayloadAction<ChatMessageImgPayload>) => {
      const cm = state.chatMessages;
      for (let i = cm.length - 1; i >= 0; i--) {
        const msg = cm[i];

        // if chatmsg was only a single link, remove it.
        if (msg.localId === action.payload.localId) {
          if (
            msg.content.length === 1 &&
            msg.content[0] &&
            msg.content[0].length === 1 &&
            msg.content[0][0].t === "t" &&
            msg.content[0][0].text.startsWith("https://") &&
            !msg.content[0][0].text.trim().includes(" ")
          ) {
            msg.content = [];
          }
          msg.imgs = action.payload.imgs;
          return;
        }
      }
    },
    chatMessageDeleted: (
      state,
      action: PayloadAction<ChatMessageIdPayload>
    ) => {
      const cm = state.chatMessages;
      for (let i = cm.length - 1; i >= 0; i--) {
        if (cm[i].id === action.payload.id) {
          cm.splice(i, 1);
          return;
        }
      }
    },
    joinedVideoChat: (state) => {
      const userId = state.self.id;
      if (!state.vcUserIds.includes(userId)) {
        state.vcUserIds.push(userId);
      }
      state.inVidChat = true;
    },
    leftVideoChat: (state) => {
      const id = state.self.id;
      state.vcUserIds = state.vcUserIds.filter((userId) => userId != id);
      state.inVidChat = false;
      state.users[id].vc.audioEnabled = false;
      state.users[id].vc.videoEnabled = false;
    },
    userJoinedVideoChat: (
      state,
      action: PayloadAction<UserJoinedVcPayload>
    ) => {
      if (!state.vcUserIds.includes(action.payload.userId)) {
        state.vcUserIds.push(action.payload.userId);
      }
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioEnabled = action.payload.audioEnabled;
      }
    },
    userLeftVideoChat: (state, action: PayloadAction<UserIdPayload>) => {
      state.vcUserIds = state.vcUserIds.filter(
        (userId) => userId !== action.payload.userId
      );
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioEnabled = false;
        user.vc.videoEnabled = false;
      }
    },
    userEnabledAudio: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioEnabled = true;
      }
    },
    // this should never be called.
    userDisabledAudio: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioEnabled = false;
      }
    },
    userEnabledVideo: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.videoEnabled = true;
      }
    },
    userDisabledVideo: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.videoEnabled = false;
      }
    },
    userAdminMuted: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.adminMuted = true;
      }
    },
    userAdminUnmuted: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.adminMuted = false;
      }
    },
    userLocalMuted: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.localMuted = true;
      }
    },
    userLocalUnmuted: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.localMuted = false;
      }
    },
    userSelfMuted: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.selfMuted = true;
      }
    },
    userSelfUnmuted: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.selfMuted = false;
      }
    },
    userVideoLocalDisabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.videoLocalDisabled = true;
      }
    },
    userAudioLocalDisabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioLocalDisabled = true;
      }
    },
    userVideoLocalEnabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.videoLocalDisabled = false;
      }
    },
    userAudioLocalEnabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioLocalDisabled = false;
      }
    },
    userVideoAdminDisabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.videoAdminDisabled = true;
      }
    },
    userAudioAdminDisabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioAdminDisabled = true;
      }
    },
    userVideoAdminEnabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.videoAdminDisabled = false;
      }
    },
    userAudioAdminEnabled: (state, action: PayloadAction<UserIdPayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.vc.audioAdminDisabled = false;
      }
    },
    userProfileImage: (
      state,
      action: PayloadAction<UserProfileImagePayload>
    ) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.profileImageUrl = action.payload.profileImageUrl;
      }
      if (state.self.id === action.payload.userId) {
        state.self.profileImageUrl = action.payload.profileImageUrl;
      }
    },
    userDisplayName: (state, action: PayloadAction<UserDisplayNamePayload>) => {
      const user = state.users[action.payload.userId];
      if (user) {
        user.displayName = action.payload.displayName;
      }

      if (state.self.id === action.payload.userId) {
        state.self.displayName = action.payload.displayName;
      }
    },
    openAlert: (state) => {
      state.alert.show = true;
    },
    closeAlert: (state) => {
      state.alert.show = false;
    },
    proxyEnabled: (state, action: PayloadAction<UserIdPayload>) => {
      state.proxy = {
        state: "on",
        userId: action.payload.userId,
      };
    },
    proxyDisabled: (state) => {
      state.proxy = {
        state: "off",
        userId: null,
      };
    },
    castleConState: (state, action: PayloadAction<RfConStatePayload>) => {
      state.castleConState = action.payload.state;
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signOut, () => {
      return initialState;
    });
  },
});

export const {
  mutedOverlay,
  doneOverlay,
  failedToConnect,
  initialUnmute,
  buffering,
  connected,
  connectError,
  roomInfo,
  showChat,
  hideChat,
  showUserContextMenu,
  hideUserContextMenu,
  theaterModeOn,
  theaterModeOff,
  volume,
  fullscreenOn,
  fullscreenOff,
  newRemoteHolder,
  requestRemote,
  remoteRequestDenied,
  cancelledRequestingRemote,
  incomingRemoteRequest,
  incomingRemoteRequestGranted,
  incomingRemoteRequestCancelled,
  userJoined,
  userLeft,
  roomClosed,
  kicked,
  kickedFromVc,
  banned,
  chatMessage,
  chatMessageConfirmed,
  chatMessageImg,
  chatMessageDeleted,
  userAdminMuted,
  userAdminUnmuted,
  userLocalMuted,
  userLocalUnmuted,
  userSelfMuted,
  userSelfUnmuted,
  joinedVideoChat,
  leftVideoChat,
  userJoinedVideoChat,
  userLeftVideoChat,
  userEnabledAudio,
  userEnabledVideo,
  userDisabledAudio,
  userDisabledVideo,
  userAudioLocalDisabled,
  userVideoLocalDisabled,
  userAudioLocalEnabled,
  userVideoLocalEnabled,
  userAudioAdminEnabled,
  userVideoAdminEnabled,
  userAudioAdminDisabled,
  userVideoAdminDisabled,
  userProfileImage,
  userDisplayName,
  closeAlert,
  proxyEnabled,
  proxyDisabled,
  castleConState,
  reset,
} = roomSlice.actions;

// export const selectUsername = (state: RootState) => state.auth.

export default roomSlice.reducer;
